import { useEffect, useState } from "react";
import { Link } from "react-router-dom";

import { Button } from "@/components/ui/Button";
import {
  commitHeldClickIds,
  discardHeldClickIds,
  readAttributionConsent,
  readClickIdsFromUrl,
  writeAttributionConsent,
} from "@/lib/attribution";
import { env } from "@/lib/env";

/**
 * Whether this page load actually carried an ad click id. Read at import —
 * the same URL `main.tsx` holds the id from, before the router can rewrite the
 * query string — so the answer survives a redirecting route and the remount
 * when the visitor moves between the home and interior layouts.
 */
const arrivedFromAd = Object.keys(readClickIdsFromUrl()).length > 0;

export function shouldAskAttributionConsent(): boolean {
  if (!env.consentRequired) return false;
  // Nothing held means nothing to store, and the copy below would be a lie.
  if (!arrivedFromAd) return false;
  if (readAttributionConsent() !== null) return false;
  return true;
}

/**
 * Asks an EU/EEA/UK visitor before storing the ad click id that brought them
 * here.
 *
 * Deliberately not a site-wide cookie banner. Analytics is refused outright in
 * these regions rather than consented to, so ad-click attribution is the only
 * non-essential thing left that touches the device — and this asks only when
 * there is actually a click id in the URL. A visitor who arrived from search,
 * a link, or direct never sees it.
 *
 * `pendingClickIds` in lib/attribution holds the id in memory until this
 * resolves; nothing is written or sent before the visitor answers.
 */
export function AttributionConsent({ onResolved }: { onResolved?: () => void }) {
  const [visible, setVisible] = useState(false);

  useEffect(() => {
    // Decided in an effect, never during render: the answer depends on
    // localStorage, which does not exist while prerendering.
    setVisible(shouldAskAttributionConsent());
  }, []);

  if (!visible) return null;

  function resolve(granted: boolean) {
    writeAttributionConsent(granted ? "granted" : "denied");
    if (granted) {
      commitHeldClickIds();
    } else {
      discardHeldClickIds();
    }
    setVisible(false);
    onResolved?.();
  }

  return (
    <section
      aria-label="Ad measurement permission"
      className="fixed inset-x-0 bottom-0 z-50 px-4 pb-4 sm:px-6 sm:pb-6"
    >
      <div className="mx-auto flex max-w-3xl flex-col gap-4 rounded-card border border-border bg-card/95 p-5 shadow-editorial backdrop-blur sm:flex-row sm:items-center sm:gap-6">
        <div className="min-w-0">
          <p className="font-medium text-foreground">Can we measure this ad?</p>
          <p className="mt-1 text-sm leading-relaxed text-muted-foreground">
            You arrived from an ad. With your permission we will store its click identifier on your
            device and send it if you join the waitlist, so we can tell which ad worked. It is not
            used to profile you, and we run no other tracking here. Read our{" "}
            <Link
              to="/legal/privacy"
              className="text-primary underline decoration-primary/40 hover:decoration-primary"
            >
              privacy policy
            </Link>
            .
          </p>
        </div>
        <div className="flex shrink-0 gap-3">
          <Button variant="secondary" size="sm" type="button" onClick={() => resolve(false)}>
            No thanks
          </Button>
          <Button variant="primary" size="sm" type="button" onClick={() => resolve(true)}>
            Allow
          </Button>
        </div>
      </div>
    </section>
  );
}
